import { Box, Divider, MultiSelect, Switch, Text } from "@mantine/core";
import React, { useEffect, useMemo } from "react";
import useStyles from "./style";
import ActionButtons from "./ActionButtons";
import { getInitialAdminPermissions, initialStaffPermissions, initialContractorPermissions, PERMISSION_LABELS } from "../../utils";
import { useSelector } from "react-redux";
import { getDocumentTemplates } from "../../services/compliance";

/**
 * Component to manage staff roles and display permissions.
 * @param {Object} props
 * @param {string} props.activeTab - Contains the selected staff's data info.
 * @param {Object} props.data - Contains the selected staff's data info.
 * @param {Object} props.mode - Contains the mode of the component like view or edit.
 * @param {Object} props.role - Contains the current role of the staff.
 * @param {Function} props.setRole - Function to set the role of the staff.
 * @param {Function} props.handleSave - Function to handle save action.
 * @param {Function} props.handleCancel - Function to handle cancel action.
 * @param {String} props.activeTab - Contains the selected tab.
 * @param {Boolean} props.roleError - Function to handle cancel action.
 */

export const StaffRolePermissionTab = ({
  data,
  mode,
  role,
  form,
  setRole,
  handleSave,
  handleCancel,
  activeTab,
  roleError,
  userpermissions,
  setUserPermissions,
}) => {

  const { classes, cx } = useStyles();
  const billing = useSelector((state) => {
    return state.featureFlags.billing;
  });

  // Client document folders configured for this org — the pool a Super Admin
  // picks from when restricting an Admin's access to client documents.
  const documentTemplates = useSelector(
    (state) => state.compliance?.documentTemplates
  );
  useEffect(() => {
    if (!documentTemplates) getDocumentTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restrictedFolders = userpermissions?.restrictedClientFolders || [];

  const clientFolderOptions = useMemo(() => {
    const names = (documentTemplates?.client || []).map((f) => f.folder);
    // Keep already-restricted names selectable even if the folder was since
    // renamed or removed from the template set, so saving doesn't drop them.
    return [...new Set([...names, ...restrictedFolders])]
      .filter(Boolean)
      .map((name) => ({ value: name, label: name }));
  }, [documentTemplates, restrictedFolders]);

  // Initialize role based on valid values
  useEffect(() => {
    const validRoles = ["admin", /* "manager", */ "staff", "contractor"];
    if (validRoles.includes(data?.role)) {
      setRole(data.role);
    }

    if (data?.permissions && typeof data.permissions === "object") {
      // Merge defaults with backend data (in case some keys are missing).
      // Null/undefined values are dropped so they fall back to the default
      // rather than being merged in and written straight back on save — legacy
      // records can hold a null aiAccess from the old facility-update sync.
      const stored = Object.fromEntries(
        Object.entries(data.permissions).filter(
          ([, value]) => value !== null && value !== undefined
        )
      );
      const mergedPermissions = {
        ...userpermissions,
        ...stored,
      };
      setUserPermissions(mergedPermissions);
    }
  }, [data?.role]);

  // Handle role toggle
  const handleRoleChange = (newRole) => {
    if (role !== newRole) {
      setRole(newRole);
      if (newRole === "admin") {
        setUserPermissions(getInitialAdminPermissions(data));
      // } else if (newRole === "manager") {
      //   setUserPermissions(initialManagerPermissions);
      } else if (newRole === "contractor") {
        setUserPermissions(initialContractorPermissions);
      } else {
        setUserPermissions(initialStaffPermissions);
      }
    }
  };

  const handlePermissionToggle = (key) => {
    setUserPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const staffHiddenKeys = ["invoice", "billing", "staff", "facility"];
  const filteredPermissions = Object.entries(userpermissions).filter(
    ([key, value]) => {
      // Not a module toggle — rendered as its own folder picker below.
      if (key === "restrictedClientFolders" || Array.isArray(value)) return false;
      // if (key === "billing" && !billing) return false;
      if (role === "staff" && staffHiddenKeys.includes(key)) return false;
      if (key === "medicationManagement" && role !== "staff") return false;
      if (key === "noteSettings" && role !== "admin") return false;
      // Command Center finance access is an Admin-only control (a plain "staff"
      // role has no Command Center).
      if (key === "finance" && role !== "admin") return false;
      return true;
    }
  );

  return (
    <Box>
      <Box className={classes.staffInfoContainer}>
        <Box px={20} pb={20}>
          <Box className={classes.rolePermissionLayout}>
            {/* Roles Column */}
            <Box className={classes.rolesColumn}>
              <Text className={classes.columnHeader}>Roles</Text>
              {["admin", /* "manager", */ "staff", /*"contractor" */ ].map((roleType) => (
                <Box
                  key={roleType}
                  className={cx(classes.roleCard, {
                    [classes.roleCardActive]: role === roleType,
                  })}
                  onClick={() => mode !== "view" && handleRoleChange(roleType)}
                >
                  <Switch
                    size="md"
                    color="yellow"
                    disabled={mode === "view"}
                    label={roleType.charAt(0).toUpperCase() + roleType.slice(1)}
                    labelPosition="left"
                    checked={role === roleType}
                    onChange={() => handleRoleChange(roleType)}
                    className={classes.swites}
                  />
                </Box>
              ))}
            </Box>

            {/* Permissions Column */}
            <Box className={classes.permissionsColumn}>
              <Text className={classes.columnHeader}>Permissions</Text>
              {filteredPermissions?.map(([key, value]) => (
                <Box key={key}>
                  <Switch
                    size="md"
                    color="yellow"
                    disabled={mode === "view"}
                    label={PERMISSION_LABELS[key] || key}
                    labelPosition="right"
                    checked={value === true}
                    onChange={() => handlePermissionToggle(key)}
                    className={classes.Permissionswites}
                  />
                </Box>
              ))}
              <Switch
                key="Enable 2FA"
                size="md"
                color="yellow"
                disabled={mode === "view"}
                label="Enable 2FA"
                labelPosition="right"
                checked={form.values.is2FaAccess}
                onChange={() => form.setFieldValue("is2FaAccess", !form.values.is2FaAccess)}
                className={classes.Permissionswites}
              />

              {/* Client document folders this Admin may not open. Admin-only —
                  a plain "staff" role reaches client documents differently. */}
              {role === "admin" && (
                <Box mt={16}>
                  <MultiSelect
                    label="Restricted client document folders"
                    description="Folders selected here are hidden from this Admin on every client. Leave empty for full access."
                    placeholder={
                      restrictedFolders.length > 0 ? undefined : "No folders restricted"
                    }
                    data={clientFolderOptions}
                    value={restrictedFolders}
                    disabled={mode === "view"}
                    searchable
                    clearable
                    creatable
                    getCreateLabel={(query) => `+ Restrict "${query}"`}
                    // Mantine selects the returned item itself, which fires
                    // onChange below — clientFolderOptions then picks the name
                    // up from the saved list, so no manual data push is needed.
                    onCreate={(query) => ({ value: query, label: query })}
                    onChange={(values) =>
                      setUserPermissions((prev) => ({
                        ...prev,
                        restrictedClientFolders: values,
                      }))
                    }
                    styles={{ label: { fontFamily: "Inter", fontWeight: 500 } }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {mode === "view" ? null : (
          <Box>
            <Divider my={10} />
            <ActionButtons
              onCancel={handleCancel}
              onSave={handleSave}
              type="submit"
              activeTab={activeTab}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
